/**
 * C7 — the client half of the required-field checklist. The available-actions response carries,
 * per action, the transition's unconditional required fields and its ConditionalRequiredFields
 * rules (field + canonical AST condition). This module re-derives "which fields are missing"
 * from the CURRENT form data so the actions-menu checklist reflects unsaved edits before submit;
 * `missingFields` from the server stays the verdict on the saved entity.
 *
 * Presence mirrors the server's RequiredFieldProbe: null/undefined, a whitespace-only string,
 * an empty Guid and a default DateTime are absent; everything else (0, false) is present.
 */
import { ConditionEvaluationError, evaluateCondition, readField, type ConditionData } from '@/core/rules/conditionEvaluator';
import type { ConditionDto } from '@/types/core/Rules';

export interface ConditionalRequiredField {
  fieldPath: string;
  /** ConditionDto JSON, normalized by the server at authoring */
  condition: string;
  message?: string;
}

export interface RequiredFieldsSource {
  requiredFields?: string[];
  conditionalRequiredFields?: ConditionalRequiredField[];
}

export interface ChecklistAction extends RequiredFieldsSource {
  enabled: boolean;
  reason?: string;
  missingFields?: string[];
  /**
   * False when the user cannot see every field the policy reads. A hidden field is stripped to its
   * default before the DTO leaves the server, so form data cannot distinguish it from an empty one.
   */
  checklistEvaluable?: boolean;
}

const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';
const DEFAULT_DATETIME_PREFIX = '0001-01-01T00:00:00';

export function hasFieldValue(data: ConditionData, fieldPath: string): boolean {
  let current: unknown = data;
  for (const segment of fieldPath.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return false;
    }
    current = readField(current as ConditionData, segment);
  }
  return isPresent(current);
}

function isPresent(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    const text = value.trim();
    return text !== '' && text !== GUID_EMPTY && !text.startsWith(DEFAULT_DATETIME_PREFIX);
  }
  return true;
}

/**
 * Unconditional fields plus every conditional field whose condition holds, in rule order,
 * without duplicates. Throws ConditionEvaluationError when a condition cannot be evaluated —
 * the same situations in which the server refuses to answer (X1: never "not required").
 */
export function resolveRequiredFields(source: RequiredFieldsSource, entityName: string, data: ConditionData): string[] {
  const required = [...(source.requiredFields ?? [])];
  for (const rule of source.conditionalRequiredFields ?? []) {
    const condition = JSON.parse(rule.condition) as ConditionDto;
    if (evaluateCondition(condition, entityName, data) && !required.includes(rule.fieldPath)) {
      required.push(rule.fieldPath);
    }
  }
  return required;
}

export function computeMissingFields(source: RequiredFieldsSource, entityName: string, data: ConditionData): string[] {
  return resolveRequiredFields(source, entityName, data).filter((field) => !hasFieldValue(data, field));
}

/**
 * Re-derives an action's `enabled`, `missingFields` and `reason` from the current form data.
 * Without form data, or when the action carries no required-field policy, the server verdict is
 * returned untouched. If a condition cannot be evaluated the server verdict is kept and the error
 * is logged — the server would have refused the same command loudly.
 */
export function applyClientChecklist<T extends ChecklistAction>(
  action: T,
  entityName: string,
  data: ConditionData | undefined,
  missingLabel: (fields: string[]) => string
): T {
  const hasPolicy = (action.requiredFields?.length ?? 0) > 0 || (action.conditionalRequiredFields?.length ?? 0) > 0;
  if (!data || !hasPolicy) {
    return action;
  }

  // The server said the form cannot answer this: at least one field the policy reads is hidden for
  // this user, so it arrives stripped to its default and is indistinguishable from empty. Guessing
  // from it would report a filled field as missing, or evaluate a condition over a value that is not
  // the real one. The server verdict on the saved entity is the honest answer.
  if (action.checklistEvaluable === false) {
    return action;
  }

  // The server probes required fields ONLY for an action whose permission and state transition
  // already passed, so a non-empty server list means "blocked solely by fields" and the client may
  // re-enable it once the form fills the gap. An action the server disabled for any other reason
  // comes back with an empty list, and the checklist has no business touching it: someone who may
  // not ship the order must be told that, not asked for a tracking number.
  const blockedOnlyByFields = action.enabled || (action.missingFields?.length ?? 0) > 0;
  if (!blockedOnlyByFields) {
    return action;
  }

  let missing: string[];
  try {
    missing = computeMissingFields(action, entityName, data);
  } catch (error) {
    if (error instanceof ConditionEvaluationError) {
      console.error(`[requiredFieldsChecklist] ${entityName}: ${error.message}`);
      return action;
    }
    throw error;
  }

  const enabled = missing.length === 0;
  return { ...action, enabled, missingFields: missing, reason: enabled ? undefined : missingLabel(missing) };
}
